/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import DeleteIcon from "@mui/icons-material/Delete";
import { usePopup } from "@nodeon/react-components";
import { useSnackbar } from "notistack";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  AccessEnum,
  MapAreaItemDto,
  MapAreaTypeEnum,
  useMapAreaApi,
} from "~/api/GeneratedApi";
import GenericTable, {
  CustomColumn,
  CustomTableProps,
} from "~/components/GenericTable";
import PaddedBox from "~/components/PaddedBox";
import { useApiErrorHandler } from "~/utils/hooks/useApiErrorHandler";
import MapAreaCreationDialog from "./MapAreaCreationDialog";

const MapAreaPage = (): JSX.Element => {
  const { t } = useTranslation("map-areas");

  const mapAreaApi = useMapAreaApi();
  const handleApiError = useApiErrorHandler();

  const { enqueueSnackbar } = useSnackbar();

  const { confirm, show } = usePopup();
  const refetchRef = useRef<() => void>();

  const columns = React.useRef<CustomColumn<MapAreaItemDto>[]>([
    {
      title: t("name"),
      field: "name",
    },
    {
      title: t("type"),
      field: "mapAreaType",
      lookup: Object.keys(MapAreaTypeEnum)
        .filter((x) => typeof MapAreaTypeEnum[x as any] === "number")
        .reduce((prev, curr) => {
          return {
            ...prev,
            [MapAreaTypeEnum[curr as any]]: t(`map-area-type-enum:${curr}`),
          };
        }, {}),
    },
    {
      title: t("map-area-creation:project-id"),
      field: "projectID",
    },
    {
      title: t("map-area-creation:project-name"),
      field: "projectName",
    },
  ]);

  const tableProps = React.useRef<CustomTableProps<MapAreaItemDto>>({
    title: t("title"),
    actions: [
      {
        requiredAccess: AccessEnum.MapAreaUpdate,
        icon: "delete",
        tooltip: t("delete-area"),
        onClick: (_, rowData) => {
          if ("length" in rowData) return;
          confirm({
            title: t("general:are-you-sure"),
            description: t("delete-area-popup-description", {
              area: rowData.name,
            }),
            icon: <DeleteIcon />,
          }).then(() => {
            mapAreaApi
              .deleteMapArea(rowData.mapAreaId)
              .then(() => {
                refetchRef.current?.();
                enqueueSnackbar(
                  t("area-deleted", {
                    area: rowData.name,
                  }),
                  { variant: "success" },
                );
              })
              .catch(handleApiError);
          });
        },
      },
      {
        requiredAccess: AccessEnum.MapAreaUpdate,
        icon: "add",
        tooltip: t("general:add"),
        isFreeAction: true,
        onClick: () =>
          show(<MapAreaCreationDialog onSubmitted={refetchRef.current} />, {
            noDialog: true,
          }),
      },
      {
        requiredAccess: AccessEnum.MapAreaUpdate,
        icon: "edit",
        tooltip: t("general:edit"),
        onClick: (_, rowData) => {
          if (Array.isArray(rowData)) return;
          show(
            <MapAreaCreationDialog
              editableData={rowData}
              onSubmitted={() => refetchRef.current?.()}
            />,
            { noDialog: true },
          );
        },
      },
    ],
  });

  return (
    <PaddedBox p={3}>
      <GenericTable<MapAreaItemDto>
        columns={columns.current}
        fetch={mapAreaApi.getMapAreas}
        getRefetch={(r) => {
          refetchRef.current = r;
        }}
        tableProps={tableProps.current}
      />
    </PaddedBox>
  );
};

export default MapAreaPage;
